import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TriggersTemplatePagesContainer,
  Content,
  TableContainer,
} from './TriggersTemplatePages.styled';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Table, CardViewX, Dropdown, Alert, Typography } from '@ann/web-components';
import ShareModal from '../../components/ShareModal';
import ArchiveModal from "../../components/ArchiveModal";
import DuplicateModal from "../../components/DuplicateModal";
import PreviewSlideOut from '../../components/PreviewSlideOut';
import FilterBarTemplate from '../../components/FilterBarTemplate';
import { formatDateShort } from "../../utils/formatDate";

const TriggersTemplatePages = () => {
  const navigate = useNavigate();
  const mode = 'grid';
  const [currentPage, setCurrentPage] = useState(1);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [promptToArchive, setPromptToArchive] = useState(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [promptToDuplicate, setPromptToDuplicate] = useState(null);
  const [alert, setAlert] = useState(null);
  const [autocompleteValue, setAutocompleteValue] = useState("");
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  const getMenuItems = (rowData) => [
    {
      id: 1,
      label: "Duplicate",
      onClick: () => {
        setPromptToDuplicate(rowData);
        setIsDuplicateModalOpen(true);
      },
    },
    {
      id: 2,
      label: "Archive",
      onClick: () => {
        setPromptToArchive(rowData);
        setIsArchiveModalOpen(true);
      },
    },
  ];

  const headers = [
    { value: 'name',        label: 'Name' },
    { value: 'description', label: 'Description' },
    { value: 'llmModel',    label: 'Agents Using This Prompt' },
    { value: 'active',      label: 'Last Updated' },
  ];

  const showAlert = (alertObj) => {
    setAlert(alertObj);
    setTimeout(() => setAlert(null), 4000);
  };

  const handleArchivePrompt = () => {
    if (promptToArchive) {
      showAlert({
        title: 'Your prompt was archived successfully',
        message: 'You can restore it from your archive anytime.',
        severity: 'success',
      });
    }
  };

  const handleDuplicatePrompt = () => {
    if (promptToDuplicate) {
      showAlert({
        title: 'Your Prompt has been successfully duplicated',
        message: 'You can modify if you wish.',
        severity: 'success',
      });
    }
  };

  const handleAutocompleteChange = (value) => {
    setAutocompleteValue(value);
  };

  const handleAutocompleteClear = () => {
    setAutocompleteValue("");
    setAutocompleteKey((prev) => prev + 1);
  };

  const handleUseTemplate = (item) => {
    const name = item.name ?? 'Untitled Template';
    localStorage.setItem('currentTriggerName', name);
    localStorage.setItem('currentTriggerCreatedBy', item.createdBy ?? 'Template');
    localStorage.setItem('currentTriggerLastUpdated', item.active ?? '');
    navigate(`/v2/workflows/${item.uuid}`, {
      state: { triggerName: name },
    });
  };

  const tableData = [
    {
      uuid: "template-sales-pitch-001",
      name: "Sales Pitch Template",
      description: "Professional sales pitch template for B2B outreach",
      llmModel: "GPT-4",
      active: "2024-01-15T10:30:00.000Z",
      createdBy: "John Doe",
      channels: "Email",
    },
    {
      uuid: "template-customer-support-002",
      name: "Customer Support Template",
      description: "Template for handling customer support inquiries",
      llmModel: "GPT-4",
      active: "2024-01-12T14:15:00.000Z",
      createdBy: "Jane Smith",
      channels: "SMS",
    },
    {
      uuid: "template-marketing-campaign-003",
      name: "Marketing Campaign Template",
      description: "Template for marketing campaign content generation",
      llmModel: "GPT-4",
      active: "2024-01-10T09:45:00.000Z",
      createdBy: "Mike Johnson",
      channels: "Push",
    },
    {
      uuid: "template-onboarding-004",
      name: "Onboarding Template",
      description: "Template for new user onboarding sequences",
      llmModel: "GPT-4",
      active: "2024-01-08T16:20:00.000Z",
      createdBy: "Sarah Wilson",
      channels: "In-App",
    },
  ];

  const showTable = tableData.length > 0;

  return (
    <>
      {alert && (
        <Alert
          title={alert.title}
          description={alert.message}
          severity={alert.severity}
          width={"692px"}
          top={"61px"}
          right={"16px"}
          onClose={() => setAlert(null)}
        />
      )}
      <TriggersTemplatePagesContainer>
        <Content>
          <PreviewSlideOut
            open={isSlideoutOpen}
            onClose={() => {
              setIsSlideoutOpen(false);
            }}
          />
          <Breadcrumb />
          <Header />

          <FilterBarTemplate
            autocompleteValue={autocompleteValue}
            autocompleteKey={autocompleteKey}
            onAutocompleteChange={handleAutocompleteChange}
            onAutocompleteClear={handleAutocompleteClear}
          />

          <TableContainer>
            {showTable && (
              <Table
                headers={headers}
                data={tableData.map((item) => ({
                  ...item,
                  active: formatDateShort(item.active) || 'Unknown',
                  element: (
                    <div style={{ maxWidth: '26px' }}>
                      <Dropdown
                        items={getMenuItems(item)}
                        size="small"
                        type="secondary"
                      />
                    </div>
                  ),
                }))}
                mode={mode}
                gridColumns={3}
                gridNode={(item) => (
                  <CardViewX size="medium">
                    <CardViewX.Header
                      avatarSrc="https://i.pravatar.cc/40"
                      menu={
                        <Dropdown items={getMenuItems(item)} size="small" type="secondary" />
                      }
                      title={item.name}
                    />
                    <Typography category="subtitle" size="medium" weight="bold">
                      {item.llmModel || 'Template'}
                    </Typography>
                    <CardViewX.Description>{item.description}</CardViewX.Description>

                    <CardViewX.Meta>
                      <CardViewX.MetaItem icon="person" label={item.createdBy || 'Unknown'} />
                      <CardViewX.MetaItem icon="lastUpdated" label={formatDateShort(item.active) || 'Unknown'} />
                      <CardViewX.MetaItem icon="group" label="+7" />
                      <CardViewX.MetaItem icon="hub" label={item.channels || 'Template'} />
                    </CardViewX.Meta>

                    <CardViewX.Actions>
                      <div disabled={true}>
                      <CardViewX.SecondaryAction
                        label="Preview"
                        onClick={() => setIsSlideoutOpen(!isSlideoutOpen)}
                        disabled={true}
                      />
                      </div>

                      <CardViewX.PrimaryAction
                        label="Use template"
                        onClick={() => handleUseTemplate(item)}
                        disabled={true}
                      />
                    </CardViewX.Actions>
                  </CardViewX>
                )}
              />
            )}
          </TableContainer>
        </Content>
      </TriggersTemplatePagesContainer>

      <Footer
        currentPage={currentPage}
        totalPages={1}
        itemsPerPage={10}
        totalItems={4}
        onPageChange={setCurrentPage}
        disabled={false}
      />

      {selectedPrompt && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => {
            setIsShareModalOpen(false);
            setSelectedPrompt(null);
          }}
          vaultId={selectedPrompt.uuid}
          vaultName={selectedPrompt.name}
        />
      )}
      {promptToArchive && (
        <ArchiveModal
          isOpen={isArchiveModalOpen}
          onClose={() => {
            setIsArchiveModalOpen(false);
            setPromptToArchive(null);
          }}
          onArchive={handleArchivePrompt}
          promptName={promptToArchive.name}
        />
      )}
      {promptToDuplicate && (
        <DuplicateModal
          isOpen={isDuplicateModalOpen}
          onClose={() => {
            setIsDuplicateModalOpen(false);
            setPromptToDuplicate(null);
          }}
          onDuplicate={handleDuplicatePrompt}
          promptName={promptToDuplicate.name}
        />
      )}
    </>
  );
};

export default TriggersTemplatePages;
